import { isInternalUser, PAYMENT_STATUS_DISPLAY, PAYMENT_STATUS_FILTER_IDS } from '@steerfi/shared';
import { IconEye } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import type { PaginatedPaymentsResponse, PaymentWithRelations } from '@/api/payments';
import { COMPLETED_STATUS_STYLES } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { type ParsedSearch, parsePrefixedSearch } from '@/lib/parse-prefixed-search';
import {
  readRecentSearches,
  removeRecentSearch,
  saveRecentSearch,
} from '@/lib/payments-recent-searches';
import { cn, formatAmount, formatCalendarDate, formatDate } from '@/lib/utils';
import { type ColumnConfig, DataTable, type FilterConfig } from './data-table';
import type { RowActivatorLink } from './row-activator';

function searchOptionsFromParsed(parsed: ParsedSearch): {
  search_field?: 'payee' | 'payer' | 'ref';
} {
  if (parsed.mode === 'text' && parsed.searchField) {
    return { search_field: parsed.searchField };
  }
  return {};
}

const STATUS_FILTER_OPTIONS = PAYMENT_STATUS_FILTER_IDS.map((id) => ({
  value: String(id),
  label: PAYMENT_STATUS_DISPLAY[id],
}));

/** Payment method codes in use (case-insensitive match on backend) */
const PAYMENT_METHOD_OPTIONS = [
  { value: 'ACH', label: 'ACH' },
  { value: 'CHECK', label: 'Check' },
  { value: 'DIGITAL_WALLET', label: 'Digital wallet' },
];

interface PaymentRowData extends PaymentWithRelations {
  // Additional computed fields can be added here if needed
}

interface Props {
  data?: PaginatedPaymentsResponse | undefined;
  isLoading?: boolean;
  error?: string | null;
  onPaymentClick?: (payment: PaymentRowData) => void;
  /** Detail route for the row's keyboard activator; omit to fall back to a button. */
  getRowLink?: ((payment: PaymentRowData) => RowActivatorLink) | undefined;
  /** Opens a lightweight quick-view modal for the row from the actions menu. */
  onQuickView?: ((payment: PaymentRowData) => void) | undefined;
  onFilterChange?: (
    filterKey: string,
    value: string | string[],
    options?: { search_field?: 'payee' | 'payer' | 'ref' }
  ) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  /** Sort actually in effect, so the header arrow reflects the query. */
  currentSortKey?: string;
  currentSortOrder?: 'asc' | 'desc';
  onPageChange?: (page: number) => void;
  /** Seed value for the search input (used when landing from URL params). */
  initialSearchInput?: string;
  /** Current filter selections (so refresh/shared URLs render correct selections). */
  currentFilterValues?: Record<string, string | string[]>;
  /** Override for the external ref column label (from party setting external_ref_display_name) */
  externalRefLabel?: string;
}

/**
 * Get display label for payment direction
 */
const getDirectionLabel = (direction: 'DEBIT' | 'CREDIT'): string => {
  return direction === 'DEBIT' ? 'Outgoing' : 'Incoming';
};

/**
 * Get badge variant for payment direction
 */
const getDirectionVariant = (
  direction: 'DEBIT' | 'CREDIT'
): 'default' | 'secondary' | 'outline' => {
  return direction === 'DEBIT' ? 'secondary' : 'default';
};

/**
 * Get badge variant for payment priority
 */
// const getPriorityVariant = (
//     priority: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL'
// ): 'default' | 'secondary' | 'outline' => {
//     const variantMap = {
//         LOW: 'outline' as const,
//         NORMAL: 'secondary' as const,
//         HIGH: 'default' as const,
//         CRITICAL: 'default' as const,
//     };
//     return variantMap[priority];
// };

/**
 * Get badge variant for payment status
 */
const getStatusVariant = (
  statusCode: string
): 'default' | 'secondary' | 'outline' | 'completed' | 'returned' => {
  const code = statusCode.toUpperCase();

  if (code.includes('COMPLETE')) {
    return COMPLETED_STATUS_STYLES.badge;
  }
  if (code === 'RELEASED' || code === 'REISSUED') {
    return 'outline';
  }
  if (code.includes('SUCCESS') || code.includes('APPROVED')) {
    return 'default';
  }
  if (code.includes('PENDING') || code.includes('PROCESSING') || code.includes('AWAITING')) {
    return 'secondary';
  }
  if (code.includes('RETURN')) {
    return 'returned';
  }
  if (
    code.includes('FAIL') ||
    code.includes('ERROR') ||
    code.includes('REJECT') ||
    code.includes('CANCEL')
  ) {
    return 'outline';
  }

  return 'outline';
};

export const PaymentsTable = ({
  data,
  isLoading = false,
  error = null,
  onPaymentClick,
  getRowLink,
  onQuickView,
  onFilterChange,
  onSortChange,
  currentSortKey,
  currentSortOrder,
  onPageChange,
  initialSearchInput,
  currentFilterValues,
  externalRefLabel = 'External Ref',
}: Props) => {
  const user = useCurrentUser();

  // Debounced search input — skip initial render to avoid triggering a fetch on mount
  const [searchInput, setSearchInput] = useState(initialSearchInput ?? '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const isFirstRender = useRef(true);
  const skipNextSearchDebounceRef = useRef(false);
  const seededRef = useRef(false);
  const syncingSearchFromPropsRef = useRef(false);
  const searchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurSuggestionsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setRecentSearches(readRecentSearches());
  }, []);

  useEffect(() => {
    if (seededRef.current) return;
    if (initialSearchInput === undefined) return;
    seededRef.current = true;
    setSearchInput(initialSearchInput);
  }, [initialSearchInput]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (syncingSearchFromPropsRef.current) {
      syncingSearchFromPropsRef.current = false;
      return;
    }
    if (searchDebounceTimerRef.current !== null) {
      clearTimeout(searchDebounceTimerRef.current);
      searchDebounceTimerRef.current = null;
    }

    const trimmed = searchInput.trim();
    if (trimmed.length === 0) {
      if (skipNextSearchDebounceRef.current) {
        skipNextSearchDebounceRef.current = false;
      }
      onFilterChange?.('search', '', {});
      return;
    }
    if (trimmed.length < 2) {
      return;
    }
    if (skipNextSearchDebounceRef.current) {
      skipNextSearchDebounceRef.current = false;
      return;
    }
    searchDebounceTimerRef.current = setTimeout(() => {
      searchDebounceTimerRef.current = null;
      const parsed = parsePrefixedSearch(searchInput);
      onFilterChange?.('search', parsed.value, searchOptionsFromParsed(parsed));
    }, 350);
    return () => {
      if (searchDebounceTimerRef.current !== null) {
        clearTimeout(searchDebounceTimerRef.current);
        searchDebounceTimerRef.current = null;
      }
    };
  }, [searchInput, onFilterChange]);

  useEffect(() => {
    if (searchInput.length > 0) {
      setShowSuggestions(false);
    }
  }, [searchInput]);

  useEffect(() => {
    return () => {
      if (blurSuggestionsTimeoutRef.current) {
        clearTimeout(blurSuggestionsTimeoutRef.current);
      }
      if (searchDebounceTimerRef.current !== null) {
        clearTimeout(searchDebounceTimerRef.current);
        searchDebounceTimerRef.current = null;
      }
    };
  }, []);

  const recordRecentAndSearch = (raw: string) => {
    saveRecentSearch(raw);
    setRecentSearches(readRecentSearches());
  };

  const handleSuggestionPick = (suggestion: string) => {
    if (searchDebounceTimerRef.current !== null) {
      clearTimeout(searchDebounceTimerRef.current);
      searchDebounceTimerRef.current = null;
    }
    if (blurSuggestionsTimeoutRef.current) {
      clearTimeout(blurSuggestionsTimeoutRef.current);
      blurSuggestionsTimeoutRef.current = null;
    }
    skipNextSearchDebounceRef.current = true;
    setSearchInput(suggestion);
    setShowSuggestions(false);
    recordRecentAndSearch(suggestion);
    const parsed = parsePrefixedSearch(suggestion);
    onFilterChange?.('search', parsed.value, searchOptionsFromParsed(parsed));
  };

  const handleRemoveRecent = (entry: string) => {
    removeRecentSearch(entry);
    setRecentSearches(readRecentSearches());
  };

  const SUGGESTION_GROUPS: { label: string; items: string[] }[] = [
    {
      label: 'Prefix search',
      items: ['payee:SteerBridge', 'payer:Wilson LLC', 'ref:SB-CK-000042', 'amount:5000'],
    },
    {
      label: 'Quick search',
      items: ['SteerBridge', 'Wilson LLC'],
    },
  ];

  const columns: ColumnConfig<PaymentRowData>[] = [
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (item) => (
        <span className="text-muted-foreground">{formatDate(item.created_at)}</span>
      ),
    },
    {
      key: 'external_reference',
      label: externalRefLabel,
      sortable: true,
      render: (item) => (
        <span className="font-mono text-xs text-muted-foreground">
          {item.external_reference ?? '—'}
        </span>
      ),
    },
    {
      key: 'direction',
      label: 'Direction',
      sortable: true,
      render: (item) => (
        <Badge variant={getDirectionVariant(item.direction)}>
          {getDirectionLabel(item.direction)}
        </Badge>
      ),
    },
    ...(isInternalUser(user?.roles)
      ? [
          {
            key: 'payer_party',
            label: 'Payer',
            sortable: true,
            render: (item: PaymentRowData) => (
              <span className="font-medium">{item.payer_party.party_name}</span>
            ),
          },
        ]
      : []),
    {
      key: 'payee_party',
      label: 'Payee',
      sortable: true,
      render: (item) => {
        // Try payment.payee_party first, fallback to first instruction's PAYEE participant
        const payeeParticipant = item.instructions?.[0]?.participants?.find(
          (p) => p.participant_role === 'PAYEE'
        );
        const payeeName: string = (item.payee_party?.party_name ||
          payeeParticipant?.party_snapshot_json?.display_name ||
          payeeParticipant?.party_snapshot_json?.legal_name ||
          '—') as string;
        return <span className="font-medium">{payeeName}</span>;
      },
    },
    {
      key: 'amount_total',
      label: 'Amount',
      sortable: true,
      render: (item) => (
        <span className="font-semibold tabular-nums">
          {formatAmount(item.amount_total, item.currency.code)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (item) => {
        const label = item.status.name;
        return <Badge variant={getStatusVariant(item.status.code)}>{label}</Badge>;
      },
    },
    {
      key: 'payment_method',
      label: 'Method',
      sortable: false,
      render: (item) => (
        <span className="text-sm text-muted-foreground">{item.payment_method?.code || '—'}</span>
      ),
    },
    // {
    //     key: 'priority',
    //     label: 'Priority',
    //     sortable: true,
    //     render: (item) => <Badge variant={getPriorityVariant(item.priority)}>{item.priority}</Badge>,
    // },
    // {
    //     key: 'instructionCount',
    //     label: 'Instructions',
    //     sortable: false,
    //     render: (item) => (
    //         <span className="text-sm text-muted-foreground">{item.instructionCount || 0}</span>
    //     ),
    // },
    {
      key: 'item_count',
      label: 'Items',
      sortable: false,
      render: (item) => (
        <span className="text-sm text-muted-foreground">{item.item_count || 0}</span>
      ),
    },
    {
      key: 'execution_date_requested',
      label: 'Execution Date',
      sortable: true,
      render: (item) => (
        <span className="text-muted-foreground">
          {item.execution_date_requested ? formatCalendarDate(item.execution_date_requested) : '—'}
        </span>
      ),
    },
    ...(onQuickView
      ? [
          {
            key: 'actions',
            label: 'Actions',
            align: 'right' as const,
            render: (item: PaymentRowData) => (
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickView(item);
                  }}
                  title="Quick view"
                  aria-label={`Quick view payment ${item.external_id ?? item.id}`}
                >
                  <IconEye className="h-4 w-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  const filters: FilterConfig[] = [
    {
      key: 'direction',
      label: 'Direction',
      placeholder: 'Filter by direction',
      multiSelect: false,
      options: [
        { value: 'DEBIT', label: 'Outgoing (Debit)' },
        { value: 'CREDIT', label: 'Incoming (Credit)' },
      ],
    },
    {
      // key matches PaymentFilters.status_id; values are numeric IDs coerced to string for the
      // Select/MultiSelect component, then sent as-is to getPayments which serialises them via
      // status_id.toString() — the backend Zod schema uses z.coerce.number() to parse them back.
      key: 'status_id',
      label: 'Status',
      placeholder: 'Filter by status',
      multiSelect: true,
      options: STATUS_FILTER_OPTIONS,
    },
    {
      key: 'payment_method_code',
      label: 'Method',
      placeholder: 'Filter by method',
      multiSelect: false,
      options: PAYMENT_METHOD_OPTIONS,
    },
  ];

  const searchExtraFilters = (
    <div className="relative z-20 flex flex-col gap-2">
      <Label htmlFor="payment-search" className="text-sm font-medium">
        Search
      </Label>
      <div className="flex items-start gap-1.5">
        <div className="relative w-[220px]">
          <Input
            id="payment-search"
            type="search"
            placeholder="Case ID, name or amount…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const trimmed = searchInput.trim();
              if (trimmed.length === 1) {
                return;
              }
              if (searchDebounceTimerRef.current !== null) {
                clearTimeout(searchDebounceTimerRef.current);
                searchDebounceTimerRef.current = null;
              }
              if (blurSuggestionsTimeoutRef.current) {
                clearTimeout(blurSuggestionsTimeoutRef.current);
                blurSuggestionsTimeoutRef.current = null;
              }
              skipNextSearchDebounceRef.current = true;
              setShowSuggestions(false);
              if (trimmed) {
                recordRecentAndSearch(trimmed);
              }
              const parsed = parsePrefixedSearch(searchInput);
              onFilterChange?.('search', parsed.value, searchOptionsFromParsed(parsed));
            }}
            onFocus={() => {
              if (blurSuggestionsTimeoutRef.current) {
                clearTimeout(blurSuggestionsTimeoutRef.current);
                blurSuggestionsTimeoutRef.current = null;
              }
              if (searchInput === '') {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => {
              blurSuggestionsTimeoutRef.current = setTimeout(() => {
                setShowSuggestions(false);
              }, 150);
            }}
            autoComplete="off"
            className="h-9 w-full pr-2"
            aria-autocomplete="list"
            aria-expanded={showSuggestions && searchInput === ''}
            aria-controls={
              showSuggestions && searchInput === '' ? 'payment-search-suggestions' : undefined
            }
          />
          {showSuggestions && searchInput === '' ? (
            // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA listbox for non-native suggestion list
            <div
              id="payment-search-suggestions"
              role="listbox"
              className="absolute left-0 right-0 top-full z-30 mt-1 flex w-[min(100vw-2rem,22rem)] flex-col overflow-visible rounded-md border border-border bg-card shadow-md"
            >
              <div className="overflow-visible">
                {recentSearches.length > 0 ? (
                  <div>
                    <div
                      className="my-1 flex items-center gap-1.5 px-[12px] py-[2px] text-[10px] font-medium uppercase leading-none tracking-[0.05em] text-muted-foreground"
                      aria-hidden
                    >
                      <span className="h-px flex-1 bg-border" />
                      <span className="shrink-0">Recent searches</span>
                      <span className="h-px flex-1 bg-border" />
                    </div>
                    {recentSearches.map((item) => (
                      <div key={item} className="flex w-full items-center hover:bg-muted/80">
                        {/* biome-ignore lint/a11y/useSemanticElements: WAI-ARIA option for listbox item */}
                        <button
                          type="button"
                          role="option"
                          className={cn(
                            'flex min-w-0 flex-1 items-center gap-1.5 px-[12px] py-[4px] text-left text-[12px] leading-none text-foreground',
                            'focus:bg-muted/80 focus:outline-none'
                          )}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSuggestionPick(item)}
                        >
                          <span aria-hidden>🕐</span>
                          <span className="truncate">{item}</span>
                        </button>
                        <button
                          type="button"
                          className="mr-1 flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                          aria-label={`Remove "${item}" from recent searches`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveRecent(item);
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                {SUGGESTION_GROUPS.map((group) => (
                  <div key={group.label}>
                    <div
                      className="my-1 flex items-center gap-1.5 px-[12px] py-[2px] text-[10px] font-medium uppercase leading-none tracking-[0.05em] text-muted-foreground"
                      aria-hidden
                    >
                      <span className="h-px flex-1 bg-border" />
                      <span className="shrink-0">{group.label}</span>
                      <span className="h-px flex-1 bg-border" />
                    </div>
                    {group.items.map((item) => (
                      // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA option for listbox item
                      <button
                        key={item}
                        type="button"
                        role="option"
                        className={cn(
                          'flex w-full items-center gap-1.5 px-[12px] py-[4px] text-left text-[12px] leading-none text-foreground',
                          'hover:bg-muted/80 focus:bg-muted/80 focus:outline-none'
                        )}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSuggestionPick(item)}
                      >
                        <span aria-hidden>🔍</span>
                        <span>{item}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              <div className="border-t border-border px-[12px] py-1 text-[11px] leading-tight text-muted-foreground">
                💡 Try <span className="font-semibold">payee:</span>{' '}
                <span className="font-semibold">payer:</span>{' '}
                <span className="font-semibold">ref:</span> or{' '}
                <span className="font-semibold">amount:</span> for faster targeted results.
              </div>
            </div>
          ) : null}
        </div>
        <div className="group relative shrink-0 pt-0.5">
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Search help"
          >
            <span className="text-base leading-none" aria-hidden>
              ⓘ
            </span>
          </button>
          <div
            role="tooltip"
            className={cn(
              'pointer-events-none invisible absolute bottom-full right-0 left-auto z-40 mb-2',
              'min-w-[280px] w-max overflow-visible',
              'rounded-md border border-border bg-popover px-2.5 py-2 text-popover-foreground shadow-md',
              'opacity-0 transition-opacity duration-150',
              'after:pointer-events-none after:absolute after:bottom-[-6px] after:right-3 after:z-10',
              'after:h-0 after:w-0 after:border-l-[6px] after:border-r-[6px] after:border-t-[7px]',
              'after:border-l-transparent after:border-r-transparent after:border-t-[var(--popover)]',
              'after:content-[""]',
              'group-hover:visible group-hover:pointer-events-auto group-hover:opacity-100'
            )}
          >
            <p className="mb-[6px] text-xs font-semibold leading-tight text-foreground">
              Search by:
            </p>
            <div className="space-y-1 text-xs leading-tight">
              <div className="whitespace-nowrap">
                <span className="select-none text-muted-foreground" aria-hidden>
                  •{' '}
                </span>
                <span className="text-muted-foreground">Case ID</span>
                <span className="mx-1.5 text-muted-foreground">→</span>
                <code className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[0.65rem] font-semibold text-foreground">
                  ref:SB-CK-000042
                </code>
                <span className="mx-1.5 text-muted-foreground">/</span>
                <span className="font-semibold text-foreground">SB-CK-000042</span>
              </div>
              <div className="whitespace-nowrap">
                <span className="select-none text-muted-foreground" aria-hidden>
                  •{' '}
                </span>
                <span className="text-muted-foreground">Payee name</span>
                <span className="mx-1.5 text-muted-foreground">→</span>
                <code className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[0.65rem] font-semibold text-foreground">
                  payee:SteerBridge
                </code>
                <span className="mx-1.5 text-muted-foreground">/</span>
                <span className="font-semibold text-foreground">SteerBridge</span>
              </div>
              <div className="whitespace-nowrap">
                <span className="select-none text-muted-foreground" aria-hidden>
                  •{' '}
                </span>
                <span className="text-muted-foreground">Payer name</span>
                <span className="mx-1.5 text-muted-foreground">→</span>
                <code className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[0.65rem] font-semibold text-foreground">
                  payer:SteerFi
                </code>
                <span className="mx-1.5 text-muted-foreground">/</span>
                <span className="font-semibold text-foreground">SteerFi</span>
              </div>
              <div className="whitespace-nowrap">
                <span className="select-none text-muted-foreground" aria-hidden>
                  •{' '}
                </span>
                <span className="text-muted-foreground">Amount</span>
                <span className="mx-1.5 text-muted-foreground">→</span>
                <code className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[0.65rem] font-semibold text-foreground">
                  amount:5000
                </code>
                <span className="mx-1.5 text-muted-foreground">/</span>
                <span className="font-semibold text-foreground">5000</span>
              </div>
            </div>
            <div className="mt-2 border-t border-border pt-2 text-[11px] leading-tight text-muted-foreground">
              <p>
                💡 Tip: type{' '}
                <code className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[0.65rem] font-semibold text-foreground">
                  fieldname:value
                </code>{' '}
                for targeted search
              </p>
              <p className="mt-1 pl-3">
                e.g.{' '}
                <code className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[0.65rem] font-semibold text-foreground">
                  payee:SteerBridge
                </code>{' '}
                only returns payee matches
              </p>
              <p className="mt-1 pl-3">Amount search returns closest matches within ±20%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Transform PaginatedPaymentsResponse to match DataTableResponse format
  // Safely map API response to DataTable shape; guard missing pagination
  const tableData = data
    ? {
        data: data.data,
        ...(data.pagination
          ? {
              pagination: {
                page: data.pagination.page,
                pageSize: data.pagination.limit,
                total: data.pagination.total,
                totalPages: data.pagination.total_pages,
              },
            }
          : {}),
      }
    : undefined;
  if (data && !data.pagination) {
    // Non-fatal: backend returned data without pagination metadata
    // biome-ignore lint/suspicious/noConsole: using in web-app
    console.debug('[PaymentsTable] Missing pagination metadata; rendering without pager');
  }

  return (
    <DataTable
      data={tableData}
      columns={columns}
      caption="Payments"
      filters={filters}
      currentFilterValues={currentFilterValues ?? {}}
      extraFilters={searchExtraFilters}
      isLoading={isLoading}
      error={error}
      onRowClick={onPaymentClick}
      // The first column is a date, so the external ref carries the activator.
      rowActivatorColumnKey="external_reference"
      // Free to be the row header precisely because the activator sits on the
      // ref: the payee is what a reader needs repeated, not the reference.
      rowHeaderColumnKey="payee_party"
      getRowLabel={(item) => `Open payment ${item.external_reference ?? item.id}`}
      {...(getRowLink && { getRowLink })}
      {...(onFilterChange && { onFilterChange })}
      {...(onSortChange && { onSortChange })}
      {...(onPageChange && { onPageChange })}
      emptyMessage={searchInput ? 'No payments match your search' : 'No payments found'}
      loadingMessage="Loading payments..."
      // Driven by the page filter state; a default declared here is a second
      // source of truth that starts out disagreeing with the query.
      {...(currentSortKey && { currentSortKey })}
      {...(currentSortOrder && { currentSortOrder })}
    />
  );
};
